import { useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BoundedApiError } from '@bounded/api-client';
import { api, isMock } from '../api/client';
import { q } from '../api/queries';
import { AuthLayout } from '@bounded/ui/auth-layout';
import { Button, Field, Input } from '@bounded/ui/primitives';
import { ErrorNotice } from '@bounded/ui/state';
import { useT } from '../i18n';

/** Only same-origin paths; anything else (//evil, https://…) falls back to home. */
export const safeNext = (value: unknown) => typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\') ? value : undefined;
type Notice = 'verified' | 'reset' | 'check-email';

export const Route = createFileRoute('/sign-in')({
  validateSearch: (search): { next?: string; notice?: Notice } => ({
    next: safeNext(search.next),
    notice: search.notice === 'verified' || search.notice === 'reset' || search.notice === 'check-email' ? search.notice : undefined,
  }),
  component: SignIn,
});

function SignIn() {
  const t = useT(); const m = t.auth.signIn;
  const { next, notice } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const capabilities = useQuery(q.capabilities());
  const [email, setEmail] = useState(isMock ? 'rin@example.dev' : '');
  const [password, setPassword] = useState('');
  const signIn = useMutation({
    mutationFn: () => api.signIn({ email, password }),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ['me'] }); await navigate({ href: next ?? '/' }); },
  });
  const resend = useMutation({ mutationFn: () => api.resendVerification(email, '/sign-in?notice=verified') });
  const unverified = signIn.error instanceof BoundedApiError && signIn.error.code === 'EMAIL_NOT_VERIFIED';

  return (
    <AuthLayout title={m.title} footer={<>
      {capabilities.data?.signup && <p>{m.newHere} <Link to="/sign-up" search={{ next }} className="text-boundary hover:underline">{m.createAccount}</Link></p>}
      {capabilities.data?.password_reset && <p className="mt-1"><Link to="/forgot-password" className="text-boundary hover:underline">{m.forgot}</Link></p>}
    </>}>
      <form className="grid gap-5" onSubmit={(e) => { e.preventDefault(); signIn.mutate(); }}>
        {notice && <p role="status" className="rounded-[var(--radius-control)] border border-rule bg-surface px-3 py-2.5">{m.notices[notice]}</p>}
        {unverified ? (
          <div role="alert" className="grid gap-2 rounded-[var(--radius-control)] border border-ask/40 bg-ask/5 px-3 py-2.5">
            <p className="font-medium">{m.unverifiedTitle}</p>
            <p className="text-graphite">{m.unverifiedBody(email)}</p>
            {capabilities.data?.verification_email && (resend.isSuccess
              ? <p className="text-sm">{m.resent}</p>
              : <div><Button size="sm" disabled={resend.isPending} onClick={() => resend.mutate()}>{m.resend}</Button></div>)}
          </div>
        ) : signIn.error && <ErrorNotice error={signIn.error} onRetry={() => signIn.mutate()} />}
        <Field label={m.email} htmlFor="email"><Input id="email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label={m.password} htmlFor="password" hint={isMock ? m.fixturesHint : undefined}>
          <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Button type="submit" variant="primary" disabled={signIn.isPending}>{signIn.isPending ? m.submitting : m.submit}</Button>
      </form>
    </AuthLayout>
  );
}
